import React, { useCallback, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ChevronLeft,
  ShieldCheck,
  ShieldAlert,
  Shield,
  Clock,
  CircleAlert,
  CheckCircle2,
  Circle,
  RotateCw,
  ListChecks,
  Info,
  Loader2,
  TriangleAlert,
  Ban,
  ArrowRight,
  ExternalLink,
  Landmark,
  FileText,
  IdCard,
  User,
  Building,
} from 'lucide-react';
import { Header } from '../../layout/Header';
import { BottomNav } from '../../layout/BottomNav';
import { useStripeAccountStatus, useStartStripeOnboarding } from '../../../api/stripe';
import { type StripeAccount, type RequirementItem } from '../../../types/stripe';
import { toast } from '../../../utils/toast';

import { extractApiError } from '../../../utils/apiError';
function openExternalLink(url: string) {
  // A full-page redirect, not window.open — Stripe's own return_url/refresh_url
  // brings the user back here, and window.open after an awaited mutation loses
  // "user activation" in Safari/Chrome and gets silently popup-blocked.
  window.location.href = url;
}

interface StatusConfig {
  badge: string;
  badgeText: string;
  icon: React.ReactNode;
  label: string;
  description: string;
  nextStepLabel: string;
}

function getStatusConfig(account: StripeAccount): StatusConfig {
  switch (account.status) {
    case 'active':
      return {
        badge: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
        badgeText: 'Active',
        icon: <ShieldCheck className="h-5 w-5" />,
        label: 'Account Active',
        description: 'Your Stripe account is fully set up and ready to receive payments.',
        nextStepLabel: 'View Dashboard',
      };
    case 'pending':
      return {
        badge: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
        badgeText: 'Pending',
        icon: <Clock className="h-5 w-5" />,
        label: 'Under Review',
        description: 'Stripe is reviewing your account. This typically takes 1-3 business days.',
        nextStepLabel: 'Refresh Status',
      };
    case 'incomplete':
      return {
        badge: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/20',
        badgeText: 'Incomplete',
        icon: <CircleAlert className="h-5 w-5" />,
        label: 'Setup Incomplete',
        description: 'Complete the remaining steps to activate your payment account.',
        nextStepLabel: 'Continue Setup',
      };
    case 'restricted':
      return {
        badge: 'bg-red-500/10 text-red-400 border-red-500/20',
        badgeText: 'Restricted',
        icon: <Ban className="h-5 w-5" />,
        label: 'Account Restricted',
        description: 'Your account has been restricted. Please resolve the issues below.',
        nextStepLabel: 'Fix Issues',
      };
    case 'not_created':
      return {
        badge: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
        badgeText: 'Not Created',
        icon: <Shield className="h-5 w-5" />,
        label: 'Account Not Created',
        description: 'Start the onboarding process to create your Stripe account.',
        nextStepLabel: 'Start Onboarding',
      };
    default:
      return {
        badge: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
        badgeText: account.status,
        icon: <Shield className="h-5 w-5" />,
        label: 'Account Status',
        description: 'Unable to determine account status.',
        nextStepLabel: 'Refresh Status',
      };
  }
}

function getNextSteps(nextAction: string | null | undefined): string[] {
  switch (nextAction) {
    case 'complete_onboarding':
      return [
        'Complete your Stripe onboarding',
        'Provide required business information',
        'Verify your identity',
        'Add bank account details',
      ];
    case 'complete_requirements':
    case 'complete_past_due_requirements':
    case 'complete_verification':
    case 'provide_requirements':
      return [
        'Review the requirements above',
        'Complete missing information',
        'Upload required documents',
        'Wait for verification',
      ];
    case 'wait_for_verification':
      return [
        'Your documents are being reviewed',
        'Check your email for updates',
        'This process usually takes 1-3 business days',
      ];
    case 'wait_for_approval':
    case 'await_review':
      return [
        'Wait for Stripe to review your account',
        'Check your email for any additional requirements',
        'This process usually takes 1-2 business days',
      ];
    case 'create_account':
      return [
        'Click the button below to create your account',
        "You'll be redirected to Stripe to complete setup",
        'Provide your business information',
        'Return here to check your status',
      ];
    default:
      return [];
  }
}

function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

function ShadcnButton({
  children,
  onClick,
  disabled,
  variant = 'primary',
  icon: Icon,
  iconPosition = 'left',
  isLoading,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'outline' | 'destructive';
  icon?: React.ComponentType<{ className?: string }>;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
  className?: string;
}) {
  const baseClasses =
    'inline-flex items-center justify-center gap-2 text-wrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-300 disabled:pointer-events-none disabled:opacity-50 min-h-10 px-4 py-2';

  const variantClasses = {
    primary: 'bg-white text-zinc-900 hover:bg-zinc-200',
    secondary: 'bg-zinc-900 text-zinc-100 border border-zinc-800 hover:bg-zinc-800',
    outline: 'border border-zinc-800 bg-transparent text-zinc-100 hover:bg-zinc-900',
    destructive: 'bg-red-600 text-white hover:bg-red-500',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={cn(baseClasses, variantClasses[variant], className)}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : Icon && iconPosition === 'left' ? (
        <Icon className="h-4 w-4" />
      ) : null}
      {children}
      {!isLoading && Icon && iconPosition === 'right' ? <Icon className="h-4 w-4" /> : null}
    </button>
  );
}

function ShadcnBadge({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
        className,
      )}
    >
      {children}
    </span>
  );
}

function ProgressRow({ label, complete }: { label: string; complete: boolean }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-2.5">
        {complete ? (
          <CheckCircle2 className="h-4 w-4 text-zinc-100" />
        ) : (
          <Circle className="h-4 w-4 text-zinc-600" />
        )}
        <span className={cn('text-sm', complete ? 'text-zinc-100' : 'text-zinc-400')}>{label}</span>
      </div>
      <span className={cn('text-xs', complete ? 'text-zinc-400' : 'text-zinc-600')}>
        {complete ? 'Complete' : 'Pending'}
      </span>
    </div>
  );
}

function TimelineItem({ step, total, text }: { step: number; total: number; text: string }) {
  const isLast = step === total;
  return (
    <div className="flex items-start gap-3.5">
      <div className="flex flex-col items-center pt-0.5">
        <div className="flex h-6 w-6 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900">
          <span className="text-[11px] font-medium text-zinc-300">{step}</span>
        </div>
        {!isLast && <div className="mt-1 h-6 w-px bg-zinc-800" />}
      </div>
      <p className="text-sm text-zinc-400 leading-relaxed pb-4 sm:pb-6 pt-0.5">{text}</p>
    </div>
  );
}

const FIELD_LABELS: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  external_account: { label: 'Bank Account', icon: Landmark },
  'tos_acceptance.ip': { label: 'Terms of Service (IP Address)', icon: FileText },
  'tos_acceptance.date': { label: 'Terms of Service (Date)', icon: FileText },
  'individual.id_number': { label: 'ID Number', icon: IdCard },
  'individual.ssn_last_4': { label: 'SSN (Last 4 Digits)', icon: IdCard },
  'individual.first_name': { label: 'First Name', icon: User },
  'individual.last_name': { label: 'Last Name', icon: User },
  'individual.dob.day': { label: 'Date of Birth', icon: User },
  'individual.dob.month': { label: 'Date of Birth', icon: User },
  'individual.dob.year': { label: 'Date of Birth', icon: User },
  'individual.email': { label: 'Email Address', icon: User },
  'individual.phone': { label: 'Phone Number', icon: User },
  'individual.address.line1': { label: 'Street Address', icon: User },
  'individual.address.city': { label: 'City', icon: User },
  'individual.address.state': { label: 'State', icon: User },
  'individual.address.postal_code': { label: 'Postal Code', icon: User },
  'business_profile.url': { label: 'Business Website', icon: Building },
  'business_profile.mcc': { label: 'Business Category (MCC)', icon: Building },
  'business_profile.name': { label: 'Business Name', icon: Building },
  'company.name': { label: 'Company Name', icon: Building },
  'company.tax_id': { label: 'Tax ID', icon: Building },
  'company.phone': { label: 'Company Phone', icon: Building },
  'company.address.line1': { label: 'Company Address', icon: Building },
  'company.address.city': { label: 'Company City', icon: Building },
  'company.address.state': { label: 'Company State', icon: Building },
  'company.address.postal_code': { label: 'Company Postal Code', icon: Building },
};

function getFieldInfo(field: string): {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
} {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];

  if (field.startsWith('individual.')) {
    const label = field
      .replace(/^individual\./, '')
      .replace(/\./g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return { label, icon: User };
  }

  if (field.startsWith('company.') || field.startsWith('business_profile.')) {
    const label = field
      .replace(/^(company|business_profile)\./, '')
      .replace(/\./g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
    return { label, icon: Building };
  }

  const label = field
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return { label, icon: ListChecks };
}

function RequirementListItem({ requirement }: { requirement: RequirementItem }) {
  const isHigh = requirement.priority === 'high';
  const fieldInfo = getFieldInfo(requirement.field);
  const FieldIcon = fieldInfo.icon;
  return (
    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <div className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 shrink-0 mt-0.5">
        <FieldIcon className="h-4 w-4 text-zinc-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-zinc-100">{fieldInfo.label}</span>
          {isHigh && (
            <ShadcnBadge className="border-zinc-800 bg-zinc-900 text-zinc-400 shrink-0">
              Required
            </ShadcnBadge>
          )}
        </div>
        <p className="text-sm text-zinc-400 leading-relaxed mt-0.5">{requirement.description}</p>
      </div>
    </div>
  );
}

export const PaymentSetupPage: React.FC = () => {
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: account, isLoading, error, refetch, isError } = useStripeAccountStatus();

  const startOnboardingMut = useStartStripeOnboarding();

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleRefresh = useCallback(async () => {
    stopPolling();
    await refetch();
  }, [refetch, stopPolling]);

  const handleStartOnboarding = async () => {
    try {
      const result = await startOnboardingMut.mutateAsync({});
      const data = result as Record<string, unknown>;
      if (data?.onboarding_url) {
        openExternalLink(data.onboarding_url as string);
      } else if (data?.dashboard_url) {
        openExternalLink(data.dashboard_url as string);
      } else {
        toast.error('Stripe did not return a setup link. Please try again.');
      }
      if (data?.account_reconnected) {
        handleRefresh();
      }
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to start Stripe onboarding. Please try again.'));
    }
  };

  const handleOpenDashboard = async () => {
    if (account?.dashboard_url) {
      openExternalLink(account.dashboard_url);
      return;
    }
    // Fallback: dashboard_url is null/expired (Stripe login links are single-use).
    // Re-call onboarding, which may return a fresh dashboard_url — mirrors the
    // mobile app's flow at stripe_onboarding_screen.dart:663-682.
    try {
      const result = await startOnboardingMut.mutateAsync({});
      const data = result as Record<string, unknown>;
      if (data?.dashboard_url) {
        openExternalLink(data.dashboard_url as string);
        return;
      }
      if (data?.onboarding_url) {
        openExternalLink(data.onboarding_url as string);
        return;
      }
      toast.error('Stripe did not return a dashboard link. Please try again.');
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to open Stripe dashboard. Please try again.'));
    }
  };

  const handleOpenOnboardingUrl = () => {
    if (account?.onboarding_url) {
      openExternalLink(account.onboarding_url);
    } else {
      toast.error('No setup link is available yet. Please refresh and try again.');
    }
  };

  const getActionButton = () => {
    if (isLoading) return null;
    const isStarting = startOnboardingMut.isPending;

    if (!account) {
      return (
        <ShadcnButton
          onClick={handleStartOnboarding}
          isLoading={isStarting}
          className="w-full"
          icon={ArrowRight}
          iconPosition="right"
        >
          Start Stripe Onboarding
        </ShadcnButton>
      );
    }

    switch (account.next_action) {
      case 'complete_onboarding':
        return (
          <ShadcnButton
            onClick={() => {
              if (account.onboarding_url) {
                handleOpenOnboardingUrl();
              } else {
                handleStartOnboarding();
              }
            }}
            isLoading={isStarting}
            className="w-full"
            icon={ArrowRight}
            iconPosition="right"
          >
            Continue Setup
          </ShadcnButton>
        );
      case 'complete_requirements':
      case 'complete_past_due_requirements':
      case 'complete_verification':
      case 'provide_requirements':
        return (
          <ShadcnButton
            onClick={handleStartOnboarding}
            isLoading={isStarting}
            variant="destructive"
            className="w-full"
            icon={ShieldAlert}
          >
            Fix Account Issues
          </ShadcnButton>
        );
      case 'wait_for_verification':
      case 'wait_for_approval':
      case 'await_review':
        return (
          <ShadcnButton
            onClick={handleRefresh}
            variant="secondary"
            className="w-full"
            icon={RotateCw}
          >
            Refresh Status
          </ShadcnButton>
        );
      case 'create_account':
        return (
          <ShadcnButton
            onClick={handleStartOnboarding}
            isLoading={isStarting}
            className="w-full"
            icon={ArrowRight}
            iconPosition="right"
          >
            Create Account
          </ShadcnButton>
        );
      case 'account_active':
        return (
          <ShadcnButton
            onClick={handleOpenDashboard}
            isLoading={isStarting}
            variant="outline"
            className="w-full"
            icon={ExternalLink}
            iconPosition="right"
          >
            View Dashboard
          </ShadcnButton>
        );
      default:
        if (account.can_receive_payments) {
          return (
            <ShadcnButton
              onClick={handleOpenDashboard}
              isLoading={isStarting}
              variant="outline"
              className="w-full"
              icon={ExternalLink}
              iconPosition="right"
            >
              View Dashboard
            </ShadcnButton>
          );
        }
        return (
          <ShadcnButton
            onClick={handleRefresh}
            variant="secondary"
            className="w-full"
            icon={RotateCw}
          >
            Refresh Status
          </ShadcnButton>
        );
    }
  };

  const requirements = account?.user_friendly_requirements;
  const statusCfg = account ? getStatusConfig(account) : null;
  const nextSteps = getNextSteps(account?.next_action);

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-32 md:pt-28 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/profile/settings"
            className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300 transition-colors mb-4"
          >
            <ChevronLeft className="h-4 w-4" />
            Settings
          </Link>
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0 space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Payment Setup</h1>
              <p className="text-sm text-zinc-400">
                Connect your Stripe account to receive payments on Musicade.
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 transition-colors disabled:opacity-50"
              aria-label="Refresh"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Error */}
          {(isError || error) && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 shrink-0">
                  <TriangleAlert className="h-5 w-5 text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-sm font-medium text-zinc-100">Unable to connect to Stripe</h2>
                  <p className="text-sm text-zinc-400 mt-1">
                    {extractApiError(error, 'Please check your connection and try again.')}
                  </p>
                  <button
                    onClick={handleRefresh}
                    className="mt-3 text-sm font-medium text-zinc-300 hover:text-zinc-100 transition-colors flex items-center gap-1.5"
                  >
                    <RotateCw className="h-3.5 w-3.5" />
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {isLoading && !account && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-8 sm:p-12 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
              <p className="text-sm text-zinc-500">Loading account status...</p>
            </div>
          )}

          {/* Account Status */}
          {statusCfg && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
              <div className="p-5 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 shrink-0">
                    <div className="text-zinc-300 [&>svg]:h-4 [&>svg]:w-4 sm:[&>svg]:h-5 sm:[&>svg]:w-5">
                      {statusCfg.icon}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h2 className="text-base font-semibold text-zinc-50">{statusCfg.label}</h2>
                      <ShadcnBadge className={statusCfg.badge}>{statusCfg.badgeText}</ShadcnBadge>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed">{statusCfg.description}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-zinc-800 px-5 sm:px-6">
                <ProgressRow label="Onboarding" complete={account?.onboarding_completed ?? false} />
                <div className="border-t border-zinc-800/60">
                  <ProgressRow label="Payments" complete={account?.can_receive_payments ?? false} />
                </div>
              </div>

              {account?.account_id && (
                <div className="border-t border-zinc-800 px-5 sm:px-6 py-3">
                  <p className="text-xs text-zinc-600 font-mono tracking-wide">
                    {account.account_id}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Requirements */}
          {requirements && requirements.length > 0 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-6">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900">
                  <ListChecks className="h-4 w-4 text-zinc-400" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-zinc-100">Action Required</h2>
                  <p className="text-xs text-zinc-500">Complete these to activate your account</p>
                </div>
              </div>
              <div className="divide-y divide-zinc-800/60">
                {requirements.map((req, i) => (
                  <RequirementListItem key={`${req.field}-${i}`} requirement={req} />
                ))}
              </div>
            </div>
          )}

          {/* Account Details */}
          {account && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-6">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900">
                  <Info className="h-4 w-4 text-zinc-400" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-zinc-100">Account Details</h2>
                  <p className="text-xs text-zinc-500">
                    Status of payments, payouts, and verification
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-sm text-zinc-400">Details Submitted</span>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      account.details_submitted ? 'text-emerald-400' : 'text-zinc-500',
                    )}
                  >
                    {account.details_submitted ? 'Submitted' : 'Not Submitted'}
                  </span>
                </div>
                <div className="border-t border-zinc-800/60 flex items-center justify-between py-1.5">
                  <span className="text-sm text-zinc-400">Charges Enabled</span>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      account.charges_enabled ? 'text-emerald-400' : 'text-zinc-500',
                    )}
                  >
                    {account.charges_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="border-t border-zinc-800/60 flex items-center justify-between py-1.5">
                  <span className="text-sm text-zinc-400">Payouts Enabled</span>
                  <span
                    className={cn(
                      'text-xs font-medium',
                      account.payouts_enabled ? 'text-emerald-400' : 'text-zinc-500',
                    )}
                  >
                    {account.payouts_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Next Steps Timeline */}
          {nextSteps.length > 0 && !account?.can_receive_payments && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5 sm:p-6">
              <h2 className="text-sm font-semibold text-zinc-100 mb-4">What to do next</h2>
              <div className="flex flex-col">
                {nextSteps.map((step, i) => (
                  <TimelineItem key={i} step={i + 1} total={nextSteps.length} text={step} />
                ))}
              </div>
            </div>
          )}

          {/* Primary Action */}
          <div className="pt-2">{getActionButton()}</div>

          {/* About Stripe */}
          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 shrink-0">
                <Info className="h-4 w-4 sm:h-5 sm:w-5 text-zinc-400" />
              </div>
              <div>
                <h2 className="text-sm font-medium text-zinc-100 mb-1">About Stripe</h2>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Stripe is a secure payment processor that handles all transactions on Musicade.
                  Your financial information is encrypted and never stored on our servers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
  );
};
